import React, { useState, useEffect, useRef, useCallback, useMemo, useSyncExternalStore } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { qaAttr } from '@/presentation/utils/qaAttr';

interface CachedImageProps {
  src: string;
  alt?: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  placeholder?: React.ReactNode;
  errorPlaceholder?: React.ReactNode;
  onLoad?: () => void;
  onError?: () => void;
  style?: React.CSSProperties;
  useOldImageOnUrlChange?: boolean; // Keep old image visible while new one loads
  fadeInDuration?: number; // Fade in duration in milliseconds (default: 500)
  fadeOutDuration?: number; // Fade out duration in milliseconds (default: 500)
  /**
   * Optional QA element id. When provided, the rendered node carries
   * `data-qa-id` (from `qaId`) plus a `data-qa-status` reflecting load state
   * (`loading` | `ready` | `error`). Only the id is forwardable; the
   * `data-qa-status` is owned by the load lifecycle, so this component
   * intentionally narrows to a bare id rather than the full `QaAttrOptions`.
   */
  qaId?: string;
}

interface CacheEntry {
  url: string;
  lastAccessed: number;
  size?: number; // Estimated size in bytes
}

// Configuration for cache management
const CACHE_CONFIG = {
  maxEntries: 200, // Maximum number of cached images
  maxMemoryMB: 50, // Maximum memory usage in MB (approximate)
  cleanupThreshold: 0.8, // Start cleanup at 80% capacity
  evictionPercentage: 0.2, // Remove 20% of oldest entries during cleanup
  cacheName: 'expause-image-cache-v1', // Cache API storage name
  indexedDBName: 'expause-image-db', // IndexedDB name
  indexedDBStore: 'image-metadata', // IndexedDB object store
};

// In-memory cache for loaded images with LRU tracking
const imageCache = new Map<string, CacheEntry>();
const loadingPromises = new Map<string, Promise<string>>();

// ---------------------------------------------------------------------------
// Cache-bust tokens (see `bustImageCache`)
// ---------------------------------------------------------------------------

/**
 * Query parameter appended to a busted image URL. The VALUE is meaningless to
 * the storage backend — the only thing that matters is that the resulting URL
 * differs from the one the browser already resolved, since that is what makes a
 * mounted `<img>` issue a request at all.
 */
const CACHE_BUST_QUERY_PARAM = 'v';

/**
 * url -> monotonic bust token. Module-level (not component state) on purpose:
 * the token must outlive any single `CachedImage` so an avatar that unmounts and
 * remounts after the bust still resolves to the busted URL.
 */
const cacheBustTokens = new Map<string, number>();
const cacheBustSubscribers = new Set<() => void>();

const subscribeToCacheBust = (onStoreChange: () => void): (() => void) => {
  cacheBustSubscribers.add(onStoreChange);
  return () => {
    cacheBustSubscribers.delete(onStoreChange);
  };
};

const appendCacheBustToken = (src: string, token: number | undefined): string => {
  if (token === undefined || !src) return src;
  const separator = src.includes('?') ? '&' : '?';
  return `${src}${separator}${CACHE_BUST_QUERY_PARAM}=${token}`;
};

// IndexedDB for persistent metadata storage
let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Initialize IndexedDB for storing cache metadata
 */
const initDB = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(CACHE_CONFIG.indexedDBName, 1);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(CACHE_CONFIG.indexedDBStore)) {
        db.createObjectStore(CACHE_CONFIG.indexedDBStore);
      }
    };
  });

  return dbPromise;
};

/**
 * Save cache metadata to IndexedDB
 */
const saveCacheMetadata = async (url: string, entry: CacheEntry) => {
  try {
    const db = await initDB();
    const tx = db.transaction(CACHE_CONFIG.indexedDBStore, 'readwrite');
    const store = tx.objectStore(CACHE_CONFIG.indexedDBStore);
    store.put(entry, url);
  } catch (error) {
    console.warn('[ImageCache] Failed to save metadata:', error);
  }
};

/**
 * Load cache metadata from IndexedDB
 */
const loadCacheMetadata = async (): Promise<Map<string, CacheEntry>> => {
  try {
    const db = await initDB();
    const tx = db.transaction(CACHE_CONFIG.indexedDBStore, 'readonly');
    const store = tx.objectStore(CACHE_CONFIG.indexedDBStore);
    const request = store.getAllKeys();

    return new Promise((resolve) => {
      request.onsuccess = async () => {
        const keys = request.result as string[];
        const metadata = new Map<string, CacheEntry>();

        for (const key of keys) {
          const getRequest = store.get(key);
          await new Promise<void>((res) => {
            getRequest.onsuccess = () => {
              metadata.set(key, getRequest.result as CacheEntry);
              res();
            };
            getRequest.onerror = () => res();
          });
        }

        resolve(metadata);
      };
      request.onerror = () => resolve(new Map());
    });
  } catch (error) {
    console.warn('[ImageCache] Failed to load metadata:', error);
    return new Map();
  }
};

/**
 * Delete cache metadata from IndexedDB
 */
const deleteCacheMetadata = async (url: string) => {
  try {
    const db = await initDB();
    const tx = db.transaction(CACHE_CONFIG.indexedDBStore, 'readwrite');
    const store = tx.objectStore(CACHE_CONFIG.indexedDBStore);
    store.delete(url);
  } catch (error) {
    console.warn('[ImageCache] Failed to delete metadata:', error);
  }
};

// Initialize cache from persistent storage on load
let cacheInitialized = false;
const initializeCache = async () => {
  if (cacheInitialized) return;
  cacheInitialized = true;

  // Load metadata from IndexedDB
  const metadata = await loadCacheMetadata();

  // Restore metadata to memory cache
  // Images themselves are in browser's HTTP cache, we just track metadata
  metadata.forEach((entry, url) => {
    imageCache.set(url, entry);
  });
};

// Start initialization
initializeCache();

// Estimate image size based on URL (rough approximation)
const estimateImageSize = (_url: string): number => {
  // Average thumbnail size estimate: 50-200KB
  // This is a rough estimate; actual size tracking would require fetch API
  return 100 * 1024; // 100KB default estimate
};

/**
 * Calculate total estimated cache size in MB
 */
const getCacheSizeMB = (): number => {
  let totalBytes = 0;
  imageCache.forEach(entry => {
    totalBytes += entry.size || 0;
  });
  return totalBytes / (1024 * 1024);
};

/**
 * Evict oldest entries from cache using LRU policy
 */
const evictOldestEntries = async (percentageToEvict: number = CACHE_CONFIG.evictionPercentage) => {
  const entriesToRemove = Math.floor(imageCache.size * percentageToEvict);
  if (entriesToRemove === 0) return;

  // Sort by lastAccessed (oldest first)
  const sortedEntries = Array.from(imageCache.entries())
    .sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);

  // Remove oldest entries from memory and IndexedDB
  for (let i = 0; i < entriesToRemove; i++) {
    const url = sortedEntries[i][0];
    imageCache.delete(url);
    await deleteCacheMetadata(url);
  }

  console.log(`[ImageCache] Evicted ${entriesToRemove} images. Cache size: ${imageCache.size} images, ~${getCacheSizeMB().toFixed(2)}MB`);
};

/**
 * Check cache limits and perform cleanup if needed
 */
const checkAndCleanupCache = () => {
  const currentSize = imageCache.size;
  const currentMemoryMB = getCacheSizeMB();

  // Check if we're above thresholds
  const exceedsEntryLimit = currentSize >= CACHE_CONFIG.maxEntries * CACHE_CONFIG.cleanupThreshold;
  const exceedsMemoryLimit = currentMemoryMB >= CACHE_CONFIG.maxMemoryMB * CACHE_CONFIG.cleanupThreshold;

  if (exceedsEntryLimit || exceedsMemoryLimit) {
    evictOldestEntries();
  }
};

/**
 * Add or update entry in cache with LRU tracking and persistent storage
 */
const addToCache = async (url: string) => {
  const entry: CacheEntry = {
    url,
    lastAccessed: Date.now(),
    size: estimateImageSize(url),
  };

  imageCache.set(url, entry);

  // Store metadata in IndexedDB (we rely on browser's HTTP cache for actual images)
  // This approach avoids CORS issues since we're not re-fetching the image
  await saveCacheMetadata(url, entry);

  await checkAndCleanupCache();
};

/**
 * Update last accessed time for cache entry (LRU)
 */
const touchCacheEntry = async (url: string) => {
  const entry = imageCache.get(url);
  if (entry) {
    entry.lastAccessed = Date.now();
    await saveCacheMetadata(url, entry);
  }
};

/**
 * CachedImage component - React equivalent of Flutter's CachedNetworkImage
 * 
 * Features:
 * - In-memory cache to avoid re-fetching the same image with LRU eviction
 * - Browser HTTP cache leveraging (Cache-Control headers)
 * - Deduplication of concurrent requests for the same image
 * - Automatic cache cleanup when limits are exceeded
 * - Loading and error states
 * - Lazy loading support
 */
export const CachedImage: React.FC<CachedImageProps> = ({
  src,
  alt = '',
  className = '',
  loading = 'lazy',
  placeholder,
  errorPlaceholder,
  onLoad,
  onError,
  style,
  useOldImageOnUrlChange = true,
  fadeInDuration = 500,
  fadeOutDuration = 500,
  qaId,
}) => {
  const { theme } = useTheme();
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [oldImageSrc, setOldImageSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [showImage, setShowImage] = useState(false);
  const isMountedRef = useRef(true);
  const previousSrcRef = useRef<string>(src);
  const displayedSrcRef = useRef<string | null>(null);

  // Re-render this image whenever `bustImageCache` bumps the token for our URL.
  const getCacheBustToken = useCallback(() => cacheBustTokens.get(src), [src]);
  const cacheBustToken = useSyncExternalStore(subscribeToCacheBust, getCacheBustToken);

  /**
   * A busted URL that failed to load, so we can fall back to the bare `src`.
   *
   * Safety net, not the happy path: appending an unknown query parameter to a
   * public object URL is expected to be ignored by the storage backend, but if a
   * backend ever rejects it the user must still see their (stale) avatar rather
   * than a broken one — i.e. a rejected bust degrades to exactly today's
   * behaviour instead of regressing past it.
   */
  const [failedBustSrc, setFailedBustSrc] = useState<string | null>(null);

  /**
   * The URL actually handed to the browser. Identical to `src` for every image
   * that was never busted, so non-avatar callers are byte-for-byte unaffected.
   */
  const effectiveSrc = useMemo(() => {
    const bustedSrc = appendCacheBustToken(src, cacheBustToken);
    return bustedSrc === failedBustSrc ? src : bustedSrc;
  }, [src, cacheBustToken, failedBustSrc]);

  // Default placeholder using theme secondary color
  const defaultPlaceholder = (
    <div
      className={className}
      style={{
        ...style,
        backgroundColor: theme.secondary
      }}
    />
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!effectiveSrc) {
      setIsLoading(false);
      setHasError(true);
      return;
    }

    /**
     * A load failed. When it was a BUSTED url, retry on the bare `src` instead
     * of surfacing an error. Guarded by `!== src`, so a genuine failure of the
     * base URL still reports an error and the retry cannot loop.
     */
    function fallBackFromFailedBust(): boolean {
      if (effectiveSrc === src) return false;
      setFailedBustSrc(effectiveSrc);
      return true;
    }

    // Detect if URL has changed
    const urlChanged = previousSrcRef.current !== effectiveSrc;

    if (urlChanged && useOldImageOnUrlChange && displayedSrcRef.current) {
      // Start fade out of old image
      setIsFadingOut(true);
      setOldImageSrc(displayedSrcRef.current);

      // Wait for fade out to complete before loading new image
      setTimeout(() => {
        previousSrcRef.current = effectiveSrc;
        loadNewImage();
      }, fadeOutDuration);
    } else {
      previousSrcRef.current = effectiveSrc;
      loadNewImage();
    }

    function loadNewImage() {
      // Check memory cache first
      if (imageCache.has(effectiveSrc)) {
        const cacheEntry = imageCache.get(effectiveSrc)!;
        touchCacheEntry(effectiveSrc); // Update LRU
        displayedSrcRef.current = cacheEntry.url;
        setImageSrc(cacheEntry.url);
        setIsLoading(false);
        setHasError(false);
        setIsFadingOut(false);
        setOldImageSrc(null);
        // No fade in for cached images - show immediately
        setShowImage(true);
        return;
      }

      // Check if already loading this image
      const existingPromise = loadingPromises.get(effectiveSrc);
      if (existingPromise) {
        existingPromise
          .then((cachedSrc) => {
            if (isMountedRef.current) {
              displayedSrcRef.current = cachedSrc;
              setImageSrc(cachedSrc);
              setIsLoading(false);
              setHasError(false);
              setIsFadingOut(false);
              setOldImageSrc(null);
              // Fade in for newly loaded images (from network)
              setTimeout(() => setShowImage(true), 10);
            }
          })
          .catch(() => {
            if (isMountedRef.current) {
              if (fallBackFromFailedBust()) return;
              setIsLoading(false);
              setHasError(true);
              setIsFadingOut(false);
              setOldImageSrc(null);
              setShowImage(false);
            }
          });
        return;
      }

      // Load the image
      setIsLoading(true);
      setHasError(false);
      setShowImage(false);

      const loadImage = async (): Promise<string> => {
        return new Promise((resolve, reject) => {
          const img = new Image();

          img.onload = () => {
            // Cache the successful load with LRU tracking
            addToCache(effectiveSrc);
            loadingPromises.delete(effectiveSrc);
            resolve(effectiveSrc);
          };

          img.onerror = () => {
            loadingPromises.delete(effectiveSrc);
            reject(new Error(`Failed to load image: ${effectiveSrc}`));
          };

          // Start loading - browser will use HTTP cache if available
          img.src = effectiveSrc;
        });
      };

      const promise = loadImage();
      loadingPromises.set(effectiveSrc, promise);

      promise
        .then((cachedSrc) => {
          if (isMountedRef.current) {
            displayedSrcRef.current = cachedSrc;
            setImageSrc(cachedSrc);
            setIsLoading(false);
            setHasError(false);
            setIsFadingOut(false);
            setOldImageSrc(null);
            onLoad?.();
            // Fade in for newly loaded images (from network)
            setTimeout(() => setShowImage(true), 10);
          }
        })
        .catch(() => {
          if (isMountedRef.current) {
            if (fallBackFromFailedBust()) return;
            setIsLoading(false);
            setHasError(true);
            setIsFadingOut(false);
            setOldImageSrc(null);
            setShowImage(false);
            onError?.();
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveSrc, src, useOldImageOnUrlChange, fadeOutDuration, onLoad, onError]);

  // Build QA props (id + load-state status) only when a `qaId` is supplied.
  const qaStatus = hasError ? 'error' : (isLoading || !imageSrc) ? 'loading' : 'ready';
  const qaProps = qaId ? qaAttr({ id: qaId, status: qaStatus }) : {};

  if (hasError) {
    return (
      <>
        {errorPlaceholder || (
          <div
            {...qaProps}
            className={`${className} flex items-center justify-center`}
            style={{
              ...style,
              backgroundColor: theme.secondary
            }}
          >        
          </div>
        )}
      </>
    );
  }

  if (isLoading || !imageSrc) {
    // Show old image while loading new one (if available)
    if (useOldImageOnUrlChange && oldImageSrc && isFadingOut) {
      return (
        <div {...qaProps} style={{ position: 'relative', ...style }} className={className}>
          {/* Old image fading out */}
          <img
            src={oldImageSrc}
            alt={alt}
            style={{
              ...style,
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: (style?.objectFit as any) || 'cover',
              opacity: 0,
              transition: `opacity ${fadeOutDuration}ms ease-out`,
            }}
          />
          {/* Placeholder underneath */}
          <div style={{ ...style }}>
            {placeholder || defaultPlaceholder}
          </div>
        </div>
      );
    }

    return <>{placeholder || defaultPlaceholder}</>;
  }

  return (
    <img
      {...qaProps}
      src={imageSrc}
      alt={alt}
      className={className}
      loading={loading}
      style={{
        ...style,
        opacity: showImage ? 1 : 0,
        transition: `opacity ${fadeInDuration}ms ease-in`,
      }}
    />
  );
};

/**
 * Utility function to preload images into cache
 */
export const preloadImage = (src: string): Promise<void> => {
  if (imageCache.has(src)) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      addToCache(src);
      resolve();
    };
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * Utility function to clear image cache (both memory and persistent storage)
 */
export const clearImageCache = async (src?: string) => {
  if (src) {
    imageCache.delete(src);
    loadingPromises.delete(src);
    await deleteCacheMetadata(src);
  } else {
    imageCache.clear();
    loadingPromises.clear();
    cacheBustTokens.clear();
    cacheBustSubscribers.forEach((notify) => notify());

    // Clear all metadata from IndexedDB
    try {
      const db = await initDB();
      const tx = db.transaction(CACHE_CONFIG.indexedDBStore, 'readwrite');
      const store = tx.objectStore(CACHE_CONFIG.indexedDBStore);
      store.clear();
    } catch (error) {
      console.warn('[ImageCache] Failed to clear IndexedDB:', error);
    }
  }
  console.log(`[ImageCache] Cache cleared. Remaining: ${imageCache.size} images`);
};

/**
 * Force every mounted `CachedImage` rendering `src` to re-fetch and re-render.
 *
 * The web counterpart of Flutter's `CachedNetworkImage.evictFromCache`
 * (lib/presentation/utils/cache_utils.dart:42-56). Flutter can simply evict,
 * because it caches the BYTES and re-reads them on the next build. This module
 * cannot: it stores only a `{ url, lastAccessed, size }` metadata row and
 * delegates the bytes to the browser's HTTP cache (see `addToCache`), so
 * dropping a metadata row cannot change which bytes a live `<img>` displays.
 *
 * Nor is `Cache-Control: no-cache` on the object enough by itself: `no-cache`
 * forces revalidation only WHEN A REQUEST IS ISSUED, and a mounted `<img>` whose
 * `src` never changes never issues one. That is exactly the profile-avatar case
 * — the object is overwritten in place at a fixed URL (`profile/{uid}/{uid}`),
 * so the URL is identical before and after a re-upload and the browser has no
 * reason to re-request it.
 *
 * So we change the URL: a bust token is appended to the RENDER url only. It is
 * deliberately NOT folded into `UserUtils.generateUserProfileImageUrl`, whose
 * output is persisted cross-client (e.g. `peerUserProfile`, built inline in
 * `giftService.executeSendVideoCallGift`) and substring-tested by Flutter
 * (`user.dart:108-110`); a token there would corrupt stored documents.
 *
 * The metadata row is still evicted, preserving the ported eviction semantics.
 */
export const bustImageCache = async (src: string) => {
  // Strictly increasing: two busts within the same millisecond must still yield
  // a different token, or the second one would be a silent no-op.
  const previousToken = cacheBustTokens.get(src) ?? 0;
  cacheBustTokens.set(src, Math.max(Date.now(), previousToken + 1));

  await clearImageCache(src);

  cacheBustSubscribers.forEach((notify) => notify());
};

/**
 * Utility function to get cache stats
 */
export const getImageCacheStats = () => {
  const sizeMB = getCacheSizeMB();
  return {
    cachedImages: imageCache.size,
    loadingImages: loadingPromises.size,
    estimatedSizeMB: Number(sizeMB.toFixed(2)),
    maxEntries: CACHE_CONFIG.maxEntries,
    maxMemoryMB: CACHE_CONFIG.maxMemoryMB,
    cachedUrls: Array.from(imageCache.keys()),
  };
};
